import React from 'react';
import { ClipboardCheck, Clock, PackageCheck, Percent, TriangleAlert, ShoppingCart } from 'lucide-react';
import { User } from '../../../../data';
import { canSeePrices } from '../../../../core/access';
import { StatCard, StatusTag } from '../../../common';
import { computeStoreDetailAnalytics } from './storeDetailAnalytics.controller';

const TREND = {
    good: { tone: 'success', label: 'Bien' },
    warning: { tone: 'warning', label: 'Regular' },
    bad: { tone: 'danger', label: 'Bajo' }
};

/** "Bien / Regular / Bajo" pill next to a KPI value. */
const Trend = ({ trend }) => (trend ? <StatusTag tone={TREND[trend].tone}>{TREND[trend].label}</StatusTag> : null);

/** Value with a de-emphasized unit ("min", "%"). */
const WithUnit = ({ value, unit }) => (
    <>
        {value}
        <span className="ml-1 text-base font-medium text-surface-400">{unit}</span>
    </>
);

/** Inventory alert line (emergencia / quiebre). */
function InventoryAlert({ icon, tone, title, description }) {
    const chip = tone === 'danger' ? 'bg-danger-bg text-danger-fg' : 'bg-warning-bg text-warning-fg';
    return (
        <div className="flex items-center gap-3">
            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[15px] ${chip}`}>{icon}</span>
            <div>
                <span className="block text-sm font-semibold text-surface-900 tabular-nums">{title}</span>
                <span className="block text-xs text-surface-500">{description}</span>
            </div>
        </div>
    );
}

export default function StoreDetailAnalytics({ store, storeChecks, avgAuditTime: avgAuditTimeProp }) {
    const {
        totalAudits,
        avgAuditTime,
        lastAuditLabel,
        availableProducts,
        totalProducts,
        availabilityRate,
        emergencyInventory,
        outOfStock,
        productsWithPrice,
        priceCompliance,
        timeTrend,
        availabilityTrend,
        complianceTrend
    } = computeStoreDetailAnalytics(storeChecks, avgAuditTimeProp);

    return (
        <div className="flex flex-col gap-4">
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
                <StatCard label="Total Auditorías" value={totalAudits} icon={<ClipboardCheck />} tone="brand" hint={lastAuditLabel} />

                <StatCard
                    label="Tiempo Promedio"
                    value={<WithUnit value={avgAuditTime.toFixed(0)} unit="min" />}
                    icon={<Clock />}
                    tone="violet"
                    trend={<Trend trend={timeTrend} />}
                />

                <StatCard
                    label="Disponibilidad"
                    value={<WithUnit value={availabilityRate} unit="%" />}
                    icon={<PackageCheck />}
                    tone="success"
                    hint={`${availableProducts} de ${totalProducts} productos`}
                    trend={<Trend trend={availabilityTrend} />}
                />

                {canSeePrices(User.getInstance()?.userType) && (
                    <StatCard
                        label="Cumplimiento Precios"
                        value={<WithUnit value={priceCompliance} unit="%" />}
                        icon={<Percent />}
                        tone="warning"
                        hint={`${productsWithPrice} con precio`}
                        trend={<Trend trend={complianceTrend} />}
                    />
                )}
            </div>

            {/* Inventory alerts */}
            {(emergencyInventory > 0 || outOfStock > 0) && (
                <div className="shelf-card flex flex-wrap gap-x-10 gap-y-4 px-5 py-4 border-l-4 border-l-danger-solid">
                    {emergencyInventory > 0 && (
                        <InventoryAlert
                            icon={<TriangleAlert />}
                            tone="warning"
                            title={`${emergencyInventory} Productos en Emergencia`}
                            description="Bajo inventario detectado"
                        />
                    )}
                    {outOfStock > 0 && (
                        <InventoryAlert icon={<ShoppingCart />} tone="danger" title={`${outOfStock} Quiebre de Stock`} description="Productos no disponibles" />
                    )}
                </div>
            )}
        </div>
    );
}
